import * as React from "react"

import { Calendar, DatePicker, DateRangePicker, type DateStr } from "@/components/ui/date-picker"

export function DatePickerPreviewRoute(): JSX.Element {
  const [single, setSingle] = React.useState<DateStr | null>(null)
  const [range, setRange] = React.useState<{ from: DateStr | null; to: DateStr | null }>({
    from: null,
    to: null,
  })
  const [calDate, setCalDate] = React.useState<DateStr | null>("2026-08-25")

  return (
    <div className="mx-auto max-w-4xl px-8.5 py-11">
      <header className="mb-8">
        <div className="font-id text-eyebrow uppercase tracking-[0.16em] text-text-3">
          Primitive
        </div>
        <h1 className="mt-1 text-display text-text">DatePicker</h1>
        <p className="mt-2 max-w-2xl text-body text-text-2">
          Custom calendar in a popover. Replaces{" "}
          <code className="font-id text-body-sm text-text">&lt;input type="date"&gt;</code> so the
          visual language stays consistent across browsers and OSes.
        </p>
      </header>

      <Section title="Single date">
        <div className="w-64">
          <DatePicker value={single} onChange={setSingle} placeholder="Select a date" />
        </div>
        <p className="text-caption text-text-3">
          Value: <span className="font-id text-text">{single ?? "—"}</span>
        </p>
      </Section>

      <Section title="Date range">
        <div className="w-[420px]">
          <DateRangePicker
            from={range.from}
            to={range.to}
            onChange={setRange}
            fromLabel="Start"
            toLabel="End"
          />
        </div>
        <p className="text-caption text-text-3">
          Value:{" "}
          <span className="font-id text-text">
            {range.from ?? "—"} → {range.to ?? "—"}
          </span>
        </p>
      </Section>

      <Section title="Calendar (bare)">
        <div className="rounded-lg border border-border bg-surface p-3">
          <Calendar
            value={calDate}
            onSelect={(iso) => {
              setCalDate(iso)
            }}
          />
        </div>
      </Section>

      <Section title="With min / max">
        <div className="w-64">
          <DatePicker
            value={null}
            onChange={() => undefined}
            min="2026-08-15"
            max="2026-09-15"
            placeholder="Aug 15 – Sep 15, 2026"
          />
        </div>
      </Section>

      <Section title="Disabled">
        <div className="w-64">
          <DatePicker value={null} onChange={() => undefined} disabled />
        </div>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="mb-8">
      <h2 className="mb-3.5 font-id text-caption uppercase tracking-[0.16em] text-text-3">
        {title}
      </h2>
      <div className="flex flex-wrap items-center gap-4">{children}</div>
    </section>
  )
}
